import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfile, type Profile } from "@/hooks/useProfile";

interface UseSettingsResult {
  profile: Profile | null;
  email: string;
  loading: boolean;
  savingProfile: boolean;
  savingPassword: boolean;
  saveProfile: (fullName: string, company: string) => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  reload: () => void;
}

export function useSettings(): UseSettingsResult {
  const { user } = useCurrentUser();
  const { profile, loading, reload } = useProfile();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = useCallback(
    async (fullName: string, company: string) => {
      if (!user) throw new Error("请先登录后再修改资料");
      setSavingProfile(true);
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ full_name: fullName.trim() || null, company: company.trim() || null })
          .eq("id", user.id);
        if (error) throw error;
        await supabase.auth.updateUser({
          data: { full_name: fullName.trim(), company: company.trim() },
        });
        await reload();
      } finally {
        setSavingProfile(false);
      }
    },
    [user, reload],
  );

  const changePassword = useCallback(
    async (current: string, next: string) => {
      if (!user?.email) throw new Error("无法获取当前账号邮箱");
      setSavingPassword(true);
      try {
        const { error: verifyError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: current,
        });
        if (verifyError) throw new Error("当前密码不正确，请重新输入");
        const { error } = await supabase.auth.updateUser({ password: next });
        if (error) throw error;
      } finally {
        setSavingPassword(false);
      }
    },
    [user],
  );

  return {
    profile,
    email: user?.email ?? profile?.email ?? "",
    loading,
    savingProfile,
    savingPassword,
    saveProfile,
    changePassword,
    reload,
  };
}